import React from "react";
import { Box, Typography } from "@mui/material";
import { useDroppable } from "@dnd-kit/core";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { VoicesCalendarPost } from "../../../Models/voicesCalendar";
import VoicesCalendarPostChip from "./VoicesCalendarPostChip";
import { dayKey } from "./voicesCalendarDates";

interface Props {
  day: Date;
  posts: VoicesCalendarPost[];
  onOpen: (postId: number) => void;
}

const SLOT_MINUTES = 30;
const FIRST_HOUR = 7;
const LAST_HOUR = 20;

const pad = (value: number) => String(value).padStart(2, "0");

const clock = (minutes: number) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

const minutesOf = (scheduledAt: string) =>
  Number(scheduledAt.slice(11, 13)) * 60 + Number(scheduledAt.slice(14, 16));

const Slot: React.FC<{
  day: Date;
  minutes: number;
  posts: VoicesCalendarPost[];
  onOpen: Props["onOpen"];
}> = ({ day, minutes, posts, onOpen }) => {
  const { colors: workspace } = useWorkspaceColors();
  const id = `${dayKey(day)}T${clock(minutes)}`;
  const past = new Date(`${id}:00`) <= new Date();
  const { setNodeRef, isOver } = useDroppable({ id, disabled: past });
  const fullHour = minutes % 60 === 0;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "64px minmax(0, 1fr)",
        borderTop: `1px ${fullHour ? "solid" : "dashed"} ${color.gray200}`,
      }}
    >
      <Typography
        sx={{
          pt: 0.75,
          pr: 1.5,
          textAlign: "right",
          fontSize: 12,
          fontWeight: fullHour ? 600 : 400,
          color: fullHour ? color.gray700 : color.gray400,
        }}
      >
        {clock(minutes)}
      </Typography>
      <Box
        ref={setNodeRef}
        sx={{
          minHeight: 40,
          p: 0.5,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: 1,
          backgroundColor: isOver
            ? color.orange50
            : past
              ? color.gray50
              : "white",
          outline: isOver ? `1px dashed ${workspace.accent}` : "none",
          transition: "background-color 0.1s",
        }}
      >
        {posts.map((post) => (
          <Box key={post.id} sx={{ width: { xs: 200, md: 260 } }}>
            <VoicesCalendarPostChip post={post} compact={false} onOpen={onOpen} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const VoicesCalendarDayView: React.FC<Props> = ({ day, posts, onOpen }) => {
  const postMinutes = posts.map((post) => minutesOf(post.scheduled_at));
  const first = Math.min(FIRST_HOUR * 60, ...postMinutes.map((m) => m - (m % 60)));
  const last = Math.max(LAST_HOUR * 60, ...postMinutes.map((m) => m - (m % 60) + 60));

  const slots: number[] = [];
  for (let minutes = first; minutes < last; minutes += SLOT_MINUTES) slots.push(minutes);

  const inSlot = (minutes: number) =>
    posts
      .filter((post) => {
        const at = minutesOf(post.scheduled_at);
        return at >= minutes && at < minutes + SLOT_MINUTES;
      })
      .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));

  return (
    <Box sx={{ borderBottom: `1px solid ${color.gray200}` }}>
      {slots.map((minutes) => (
        <Slot
          key={minutes}
          day={day}
          minutes={minutes}
          posts={inSlot(minutes)}
          onOpen={onOpen}
        />
      ))}
    </Box>
  );
};

export default VoicesCalendarDayView;
